<template>
  <v-container fluid class="dashboard-container">
    <v-alert
      v-if="isMobile"
      color="success"
      icon="mdi-information"
      variant="tonal"
      closable
      class="mb-4 mobile-alert"
      border="start"
    >
      Auf mobilen Geräten ist das Verschieben der Widgets deaktiviert. Verwende
      ein Desktop-Gerät, um das Dashboard anzupassen.
    </v-alert>

    <!-- Animierte Hintergrundelemente -->
    <div class="background-elements">
      <div class="ambient-light ambient-light-1"></div>
      <div class="ambient-light ambient-light-2"></div>
      <div class="ambient-light ambient-light-3"></div>
      <div class="grid-overlay"></div>
    </div>

    <v-row>
      <!-- Spalten mit Draggable-Komponenten -->
      <v-col cols="12" sm="6" md="3">
        <draggable
          v-model="leftColumn"
          group="dashboard"
          item-key="id"
          :animation="200"
          handle=".drag-handle"
          :disabled="isMobile"
          class="column-dropzone"
          @end="saveLayout"
        >
          <template #item="{ element }">
            <widget-card
              :element="element"
              @toggle-collapse="toggleCollapse"
              @show-modelle-search="showModelleSearch = true"
              @show-add-modelle="showAddModelle = true"
              @show-durchwahl-search="durchwahlSearchDialog = true"
              @show-kurztext-search="kurztextSearchDialog = true"
              @show-notes-search="showNotesSearch"
              @show-add-note="showAddNote"
            />
          </template>
        </draggable>
      </v-col>

      <v-col cols="12" sm="6" md="6">
        <draggable
          v-model="middleColumn"
          group="dashboard"
          item-key="id"
          :animation="200"
          handle=".drag-handle"
          :disabled="isMobile"
          class="column-dropzone"
          @end="saveLayout"
        >
          <template #item="{ element }">
            <widget-card
              :element="element"
              @toggle-collapse="toggleCollapse"
              @show-modelle-search="showModelleSearch = true"
              @show-add-modelle="showAddModelle = true"
              @show-durchwahl-search="durchwahlSearchDialog = true"
              @show-kurztext-search="kurztextSearchDialog = true"
              @show-notes-search="showNotesSearch"
              @show-add-note="showAddNote"
            />
          </template>
        </draggable>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <draggable
          v-model="rightColumn"
          group="dashboard"
          item-key="id"
          :animation="200"
          handle=".drag-handle"
          :disabled="isMobile"
          class="column-dropzone"
          @end="saveLayout"
        >
          <template #item="{ element }">
            <widget-card
              :element="element"
              @toggle-collapse="toggleCollapse"
              @show-modelle-search="showModelleSearch = true"
              @show-add-modelle="showAddModelle = true"
              @show-durchwahl-search="durchwahlSearchDialog = true"
              @show-kurztext-search="kurztextSearchDialog = true"
              @show-notes-search="showNotesSearch"
              @show-add-note="showAddNote"
            />
          </template>
        </draggable>
      </v-col>
    </v-row>

    <!-- Modelle hinzufügen / bearbeiten Dialog -->
    <v-dialog v-model="showAddModelle" max-width="600">
      <v-card class="dialog-card model-dialog">
        <v-card-title
          class="dialog-title model-dialog-title d-flex align-center justify-space-between"
        >
          <div class="d-flex align-center">
            <v-icon color="success" class="mr-2" size="24">
              {{ isEditingModel ? "mdi-pencil" : "mdi-coffee-maker" }}
            </v-icon>
            <span class="title-text">{{
              isEditingModel ? "Modell bearbeiten" : "Neues Modell hinzufügen"
            }}</span>
          </div>
          <v-btn icon variant="text" @click="closeModelDialog" class="ml-2">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content pt-4">
          <v-form @submit.prevent="saveModel">
            <!-- Modellname -->
            <v-text-field
              v-model="newModel.modell"
              label="Modell (z.B. EA810870/XXX XX)"
              variant="outlined"
              color="success"
              bg-color="rgba(30, 30, 40, 0.5)"
              required
              hide-details="auto"
              class="form-field mb-4"
            ></v-text-field>

            <!-- WS-Menü mit Zubehör-Checkbox -->
            <div class="d-flex flex-wrap mb-2 align-center gap-3">
              <v-text-field
                v-model="newModel.ws_menue"
                label="WS-Menü (LO, RO, LU, RU)"
                variant="outlined"
                color="success"
                bg-color="rgba(30, 30, 40, 0.5)"
                hide-details="auto"
                class="form-field flex-grow-1"
              ></v-text-field>

              <v-checkbox
                v-model="newModel.ws_zubehoer"
                label="Zubehör nötig"
                hide-details
                density="compact"
                color="success"
                class="ml-2"
              ></v-checkbox>
            </div>

            <!-- Tassenzahl -->
            <v-text-field
              v-model="newModel.tassenzahl"
              label="Tassenzahl Menü"
              variant="outlined"
              color="success"
              bg-color="rgba(30, 30, 40, 0.5)"
              hide-details="auto"
              class="form-field mb-4 mt-2"
            ></v-text-field>

            <!-- Kalibrieren mit Zubehör-Checkbox -->
            <div class="d-flex flex-wrap mb-2 align-center gap-3">
              <v-select
                v-model="newModel.kalibrieren"
                :items="['Mit Cake', 'Ohne Cake']"
                label="Kalibrieren"
                variant="outlined"
                color="success"
                bg-color="rgba(30, 30, 40, 0.5)"
                hide-details="auto"
                class="form-field flex-grow-1"
              ></v-select>

              <v-checkbox
                v-model="newModel.kalzubehoer"
                label="Zubehör nötig"
                hide-details
                density="compact"
                color="success"
                class="ml-2"
              ></v-checkbox>
            </div>

            <!-- Zurücksetzen mit Zubehör-Checkbox -->
            <div class="d-flex flex-wrap mb-2 mt-2 align-center gap-3">
              <v-text-field
                v-model="newModel.zuruecksetzen"
                label="Zurücksetzen"
                variant="outlined"
                color="success"
                bg-color="rgba(30, 30, 40, 0.5)"
                hide-details="auto"
                class="form-field flex-grow-1"
              ></v-text-field>

              <v-checkbox
                v-model="newModel.zrzubehoer"
                label="Zubehör nötig"
                hide-details
                density="compact"
                color="success"
                class="ml-2"
              ></v-checkbox>
            </div>

            <!-- Temperatur-Eingabefelder -->
            <v-divider class="my-4"></v-divider>

            <!-- Gruppierte Kaffee-Temperatur-Felder mit Überschrift -->
            <div class="temperature-section">
              <h3 class="temperature-heading mb-2">
                <v-icon class="mr-1" size="small">mdi-coffee</v-icon>
                Kaffee-Temperaturen
              </h3>

              <div class="d-flex flex-wrap gap-3">
                <v-text-field
                  v-model="newModel.kaffee_temp_min"
                  label="Min. Temperatur"
                  variant="outlined"
                  color="success"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details="auto"
                  class="form-field flex-grow-1"
                  placeholder="z.B. 85"
                ></v-text-field>

                <v-text-field
                  v-model="newModel.kaffee_temp_max"
                  label="Max. Temperatur"
                  variant="outlined"
                  color="success"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details="auto"
                  class="form-field flex-grow-1"
                  placeholder="z.B. 95"
                ></v-text-field>

                <v-text-field
                  v-model="newModel.kaffee_temp_soll"
                  label="Soll-Temperatur"
                  variant="outlined"
                  color="success"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details="auto"
                  class="form-field flex-grow-1"
                  placeholder="z.B. 90"
                ></v-text-field>
              </div>
            </div>

            <!-- Gruppierte Milch-Temperatur-Felder mit Überschrift -->
            <div class="temperature-section mt-4">
              <h3 class="temperature-heading mb-2">
                <v-icon class="mr-1" size="small">mdi-cup-water</v-icon>
                Milch-Temperaturen
              </h3>

              <div class="d-flex flex-wrap gap-3">
                <v-text-field
                  v-model="newModel.milch_temp_min"
                  label="Min. Temperatur"
                  variant="outlined"
                  color="success"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details="auto"
                  class="form-field flex-grow-1"
                  placeholder="z.B. 55"
                ></v-text-field>

                <v-text-field
                  v-model="newModel.milch_temp_max"
                  label="Max. Temperatur"
                  variant="outlined"
                  color="success"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details="auto"
                  class="form-field flex-grow-1"
                  placeholder="z.B. 65"
                ></v-text-field>

                <v-text-field
                  v-model="newModel.milch_temp_soll"
                  label="Soll-Temperatur"
                  variant="outlined"
                  color="success"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details="auto"
                  class="form-field flex-grow-1"
                  placeholder="z.B. 60"
                ></v-text-field>
              </div>
            </div>

            <!-- Alte Temperatur-Felder (für Kompatibilität) -->
            <div class="temperature-section mt-4">
              <h3 class="temperature-heading mb-2">
                <v-icon class="mr-1" size="small">mdi-thermometer</v-icon>
                Alte Temperaturangaben (Legacy)
              </h3>

              <div class="d-flex flex-wrap gap-3">
                <v-text-field
                  v-model="newModel.temp_min"
                  label="Min. Temperatur (alt)"
                  variant="outlined"
                  color="success"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details="auto"
                  class="form-field flex-grow-1"
                  placeholder="z.B. 85"
                ></v-text-field>

                <v-text-field
                  v-model="newModel.temp_max"
                  label="Max. Temperatur (alt)"
                  variant="outlined"
                  color="success"
                  bg-color="rgba(30, 30, 40, 0.5)"
                  hide-details="auto"
                  class="form-field flex-grow-1"
                  placeholder="z.B. 95"
                ></v-text-field>
              </div>
            </div>

            <!-- Sonstiges -->
            <v-textarea
              v-model="newModel.sonstiges"
              label="Sonstige Informationen"
              variant="outlined"
              color="success"
              bg-color="rgba(30, 30, 40, 0.5)"
              hide-details="auto"
              auto-grow
              rows="3"
              class="form-field mt-4"
            ></v-textarea>
          </v-form>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions model-dialog-actions">
          <!-- Löschen-Button (nur bei Bearbeitung) -->
          <v-btn
            v-if="isEditingModel"
            variant="text"
            color="error"
            @click="deleteModel(newModel)"
            :disabled="loading"
            prepend-icon="mdi-delete"
            class="delete-btn"
          >
            Löschen
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn variant="text" @click="closeModelDialog" :disabled="loading">
            Abbrechen
          </v-btn>

          <v-btn
            color="success"
            variant="elevated"
            @click="saveModel"
            :loading="loading"
            :disabled="!newModel.modell"
            class="submit-btn px-4"
          >
            {{ isEditingModel ? "Aktualisieren" : "Hinzufügen" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modelle suchen Dialog -->
    <v-dialog v-model="showModelleSearch" max-width="500">
      <v-card class="dialog-card search-dialog">
        <v-card-title class="dialog-title search-dialog-title">
          <v-icon color="success" class="mr-2">mdi-coffee-maker-search</v-icon>
          <span class="title-text">Modelle durchsuchen</span>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content">
          <v-text-field
            v-model="modelSearchQuery"
            label="Suche nach Modellname"
            variant="outlined"
            color="success"
            bg-color="rgba(30, 30, 40, 0.5)"
            prepend-inner-icon="mdi-magnify"
            clearable
            hide-details
            autofocus
            class="search-input form-field"
          ></v-text-field>

          <v-list v-if="filteredModelle.length > 0" class="search-results mt-4">
            <v-list-item
              v-for="model in filteredModelle"
              :key="model.id"
              class="search-result-item"
              rounded
              @click="editModel(model)"
            >
              <div class="d-flex align-center justify-space-between w-100">
                <div class="model-info flex-grow-1">
                  <v-list-item-title class="font-weight-medium">
                    {{ model.modell }}
                  </v-list-item-title>
                  <v-list-item-subtitle
                    v-if="model.sonstiges"
                    class="text-truncate"
                  >
                    {{ truncateText(model.sonstiges, 100) }}
                  </v-list-item-subtitle>
                </div>
                <div class="model-actions">
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    color="success"
                    @click.stop="editModel(model)"
                    title="Modell bearbeiten"
                  >
                    <v-icon size="small">mdi-pencil</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-list-item>
          </v-list>

          <div
            v-else-if="modelSearchQuery"
            class="text-center empty-results py-4"
          >
            <v-icon size="40" color="grey" class="mb-2"
              >mdi-coffee-maker-off</v-icon
            >
            <div class="text-grey">Keine Modelle gefunden</div>
          </div>

          <div
            v-else
            class="text-center search-placeholder py-4 text-medium-emphasis"
          >
            Geben Sie einen Suchbegriff ein, um Modelle zu finden
          </div>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions search-dialog-actions">
          <v-btn
            variant="text"
            prepend-icon="mdi-plus"
            color="success"
            @click="
              showAddModelle = true;
              showModelleSearch = false;
            "
            class="add-btn"
          >
            Neues Modell
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn
            variant="text"
            @click="
              showModelleSearch = false;
              modelSearchQuery = '';
            "
          >
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modell löschen Bestätigung -->
    <v-dialog v-model="showDeleteConfirm" max-width="400">
      <v-card class="dialog-card confirm-dialog">
        <v-card-title class="dialog-title confirm-dialog-title warning-header">
          <v-icon color="error" class="mr-2">mdi-alert</v-icon>
          <span class="title-text">Modell löschen?</span>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content pt-3">
          Sind Sie sicher, dass Sie das Modell
          <strong>{{ modelToDelete?.modell }}</strong> löschen möchten? Diese
          Aktion kann nicht rückgängig gemacht werden.
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions confirm-dialog-actions">
          <v-spacer></v-spacer>

          <v-btn
            variant="text"
            @click="showDeleteConfirm = false"
            :disabled="loading"
          >
            Abbrechen
          </v-btn>

          <v-btn
            color="error"
            variant="elevated"
            @click="confirmDelete"
            :loading="loading"
            class="delete-confirm-btn"
          >
            <v-icon class="mr-1">mdi-delete</v-icon>
            Löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Durchwahl Suchen Dialog -->
    <v-dialog v-model="durchwahlSearchDialog" max-width="500">
      <v-card class="dialog-card search-dialog">
        <v-card-title class="dialog-title search-dialog-title">
          <v-icon color="success" class="mr-2">mdi-phone-search</v-icon>
          <span class="title-text">Durchwahlen durchsuchen</span>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content">
          <v-text-field
            v-model="durchwahlSearchQuery"
            label="Suche nach Name, Vorname oder Durchwahl"
            variant="outlined"
            color="success"
            bg-color="rgba(30, 30, 40, 0.5)"
            prepend-inner-icon="mdi-magnify"
            clearable
            hide-details
            autofocus
            class="search-input form-field"
          ></v-text-field>

          <v-list
            v-if="filteredDurchwahlen.length > 0"
            class="search-results mt-4"
          >
            <v-list-item
              v-for="durchwahl in filteredDurchwahlen"
              :key="durchwahl.id"
              class="search-result-item"
              rounded
            >
              <div class="d-flex align-center justify-space-between w-100">
                <div class="extension-info">
                  <v-list-item-title class="extension-name">
                    {{ durchwahl.name }}
                    {{ durchwahl.vorname ? durchwahl.vorname : "" }}
                  </v-list-item-title>
                  <v-list-item-subtitle
                    v-if="durchwahl.abteilung"
                    class="extension-department"
                  >
                    {{ durchwahl.abteilung }}
                  </v-list-item-subtitle>
                </div>
                <div class="extension-number">
                  <v-chip
                    size="small"
                    class="extension-chip"
                    variant="outlined"
                    color="success"
                    title="Durchwahl kopieren"
                    @click="copyToClipboard(durchwahl.durchwahl)"
                  >
                    <v-icon size="x-small" class="mr-1">mdi-phone</v-icon>
                    {{ durchwahl.durchwahl }}
                  </v-chip>
                </div>
              </div>
            </v-list-item>
          </v-list>

          <div
            v-else-if="durchwahlSearchQuery"
            class="text-center empty-results py-4"
          >
            <v-icon size="40" color="grey" class="mb-2">mdi-phone-off</v-icon>
            <div class="text-grey">Keine Durchwahlen gefunden</div>
          </div>

          <div
            v-else
            class="text-center search-placeholder py-4 text-medium-emphasis"
          >
            Geben Sie einen Suchbegriff ein, um Durchwahlen zu finden
          </div>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions search-dialog-actions">
          <v-spacer></v-spacer>

          <v-btn
            variant="text"
            @click="
              durchwahlSearchDialog = false;
              durchwahlSearchQuery = '';
            "
          >
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Kurztexte Suchen Dialog -->
    <v-dialog v-model="kurztextSearchDialog" max-width="500">
      <v-card class="dialog-card search-dialog">
        <v-card-title class="dialog-title search-dialog-title">
          <v-icon color="success" class="mr-2">mdi-text-search</v-icon>
          <span class="title-text">Kurztexte durchsuchen</span>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content">
          <v-text-field
            v-model="searchQuery"
            label="Suche nach Nummer oder Inhalt"
            variant="outlined"
            color="success"
            bg-color="rgba(30, 30, 40, 0.5)"
            prepend-inner-icon="mdi-magnify"
            clearable
            hide-details
            autofocus
            class="search-input form-field"
          ></v-text-field>

          <v-list
            v-if="filteredKurztexte.length > 0"
            class="search-results mt-4"
          >
            <v-list-item
              v-for="kurztext in filteredKurztexte"
              :key="kurztext.id"
              class="search-result-item"
              rounded
            >
              <div class="d-flex align-center justify-space-between w-100">
                <div class="snippet-info flex-grow-1">
                  <div class="d-flex align-center">
                    <v-chip
                      size="x-small"
                      class="snippet-number mr-2"
                      variant="outlined"
                      color="success"
                      title="Nummer"
                    >
                      {{ kurztext.nummer }}
                    </v-chip>
                    <v-list-item-title class="snippet-title text-truncate">
                      {{ kurztext.bezeichnung || "Ohne Bezeichnung" }}
                    </v-list-item-title>
                  </div>
                  <v-list-item-subtitle class="snippet-text text-truncate">
                    {{ truncateText(kurztext.kurztext, 120) }}
                  </v-list-item-subtitle>
                </div>
                <div class="snippet-actions ml-2">
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    color="success"
                    title="Kurztext kopieren"
                    @click="copyToClipboard(kurztext.kurztext)"
                  >
                    <v-icon size="small">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-list-item>
          </v-list>

          <div v-else-if="searchQuery" class="text-center empty-results py-4">
            <v-icon size="40" color="grey" class="mb-2"
              >mdi-text-box-remove</v-icon
            >
            <div class="text-grey">Keine Kurztexte gefunden</div>
          </div>

          <div
            v-else
            class="text-center search-placeholder py-4 text-medium-emphasis"
          >
            Geben Sie einen Suchbegriff ein, um Kurztexte zu finden
          </div>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions search-dialog-actions">
          <v-spacer></v-spacer>

          <v-btn
            variant="text"
            @click="
              kurztextSearchDialog = false;
              searchQuery = '';
            "
          >
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Notizen Suchen Dialog -->
    <v-dialog v-model="showNotesSearchDialog" max-width="500">
      <v-card class="dialog-card search-dialog">
        <v-card-title class="dialog-title search-dialog-title">
          <v-icon color="success" class="mr-2">mdi-note-search</v-icon>
          <span class="title-text">Notizen durchsuchen</span>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content">
          <v-text-field
            v-model="notesSearchQuery"
            label="Suche nach Notizen"
            variant="outlined"
            color="success"
            bg-color="rgba(30, 30, 40, 0.5)"
            prepend-inner-icon="mdi-magnify"
            clearable
            hide-details
            autofocus
            class="search-input form-field"
          ></v-text-field>

          <v-list
            v-if="filteredNotes.length > 0"
            class="search-results mt-4"
          >
            <v-list-item
              v-for="note in filteredNotes"
              :key="note.id"
              class="search-result-item"
              rounded
            >
              <div class="d-flex align-center justify-space-between w-100">
                <div class="note-info flex-grow-1">
                  <v-list-item-title class="font-weight-medium">
                    {{ note.title || "Ohne Titel" }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-truncate">
                    {{ truncateText(note.content, 100) }}
                  </v-list-item-subtitle>
                </div>
                <div class="note-actions">
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    color="success"
                    @click="editNote(note)"
                    title="Notiz bearbeiten"
                  >
                    <v-icon size="small">mdi-pencil</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-list-item>
          </v-list>

          <div
            v-else-if="notesSearchQuery"
            class="text-center empty-results py-4"
          >
            <v-icon size="40" color="grey" class="mb-2">mdi-note-off</v-icon>
            <div class="text-grey">Keine Notizen gefunden</div>
          </div>

          <div
            v-else
            class="text-center search-placeholder py-4 text-medium-emphasis"
          >
            Geben Sie einen Suchbegriff ein, um Notizen zu finden
          </div>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions search-dialog-actions">
          <v-btn
            variant="text"
            prepend-icon="mdi-plus"
            color="success"
            @click="
              showAddNoteDialog = true;
              showNotesSearchDialog = false;
            "
            class="add-btn"
          >
            Neue Notiz
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn
            variant="text"
            @click="
              showNotesSearchDialog = false;
              notesSearchQuery = '';
            "
          >
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Notiz hinzufügen/bearbeiten Dialog -->
    <v-dialog v-model="showAddNoteDialog" max-width="500">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon color="success" class="mr-2" size="24">
              {{ editingNote ? "mdi-pencil" : "mdi-note-plus" }}
            </v-icon>
            <span class="title-text">
              {{ editingNote ? "Notiz bearbeiten" : "Neue Notiz hinzufügen" }}
            </span>
          </div>
          <v-btn icon variant="text" @click="closeNoteDialog" class="ml-2">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content pt-4">
          <v-form @submit.prevent="saveNote">
            <v-text-field
              v-model="newNote.title"
              label="Titel der Notiz"
              variant="outlined"
              color="success"
              bg-color="rgba(30, 30, 40, 0.5)"
              hide-details="auto"
              class="form-field mb-4"
            ></v-text-field>

            <v-textarea
              v-model="newNote.content"
              label="Inhalt der Notiz"
              variant="outlined"
              color="success"
              bg-color="rgba(30, 30, 40, 0.5)"
              hide-details="auto"
              auto-grow
              rows="5"
              class="form-field"
            ></v-textarea>
          </v-form>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions">
          <v-btn
            v-if="editingNote"
            variant="text"
            color="error"
            @click="deleteNote"
            :disabled="loading"
            prepend-icon="mdi-delete"
            class="delete-btn"
          >
            Löschen
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn variant="text" @click="closeNoteDialog" :disabled="loading">
            Abbrechen
          </v-btn>

          <v-btn
            color="success"
            variant="elevated"
            @click="saveNote"
            :loading="loading"
            :disabled="!newNote.content"
            class="submit-btn px-4"
          >
            {{ editingNote ? "Aktualisieren" : "Hinzufügen" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, reactive, onBeforeUnmount } from "vue";
import axios from "axios";
import Holidays from "date-holidays";
import draggable from "vuedraggable";
import { API_BASE_URL } from "../config";
import { useToast } from "vue-toastification";
import { provide } from "vue";
import WidgetCard from "@/components/WidgetCard.vue";

function toggleShowAllUpcoming() {
  showAllUpcoming.value = !showAllUpcoming.value;
}

// ======== Basis-Setup ========
const toast = useToast();
const loading = ref(false);

// ======== Dashboard-Daten ========
const dashboardData = ref({
  daily: {
    reparatur: 0,
    austausch: 0,
    kostenvoranschlag: 0,
    unrepariert: 0,
    a_b_ware: 0,
    schrott: 0,
    total: 0,
  },
  monthly: {
    reparatur: 0,
    austausch: 0,
    kostenvoranschlag: 0,
    unrepariert: 0,
    a_b_ware: 0,
    schrott: 0,
    total: 0,
  },
  user: { vorname: "" },
});

// Hilfsfunktionen für die Statistik-Widgets hinzufügen

// Formatierung der Uhrzeit für die digitale Anzeige
function formatTimeDisplay(dateTime) {
  if (!dateTime) return "--:--:--";
  const hours = dateTime.getHours().toString().padStart(2, "0");
  const minutes = dateTime.getMinutes().toString().padStart(2, "0");
  const seconds = dateTime.getSeconds().toString().padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
}

// Labels für die Statistiken
function getStatLabel(key) {
  const labels = {
    reparatur: "Reparatur",
    austausch: "Austausch",
    kostenvoranschlag: "KVA",
    unrepariert: "Unrepariert",
    a_b_ware: "A/B Ware",
    schrott: "Schrott",
    retouren_sorting: "Retouren Sorting", // Ergänzt für Retouren Sorting
    total: "Gesamt",
  };
  return labels[key] || key;
}

// Farben für die Statistiken
function getStatColor(key) {
  const colors = {
    reparatur: "#4CAF50", // Grün
    austausch: "#2196F3", // Blau
    kostenvoranschlag: "#FF9800", // Orange
    unrepariert: "#F44336", // Rot
    a_b_ware: "#9C27B0", // Lila
    schrott: "#607D8B", // Grau-Blau
    retouren_sorting: "#795548", // Braun für Retouren Sorting
  };
  return colors[key] || "#CCCCCC";
}

// Prozentsatz für die Fortschrittsbalken berechnen
function calculatePercentage(value, total) {
  if (!total || total === 0) return 0;
  return (value / total) * 100;
}

// ======== Globale Listen & Flags ========
// Durchwahlen
const globalDurchwahlen = ref([]);
const showAllDurchwahlen = ref(false);
const durchwahlSearchDialog = ref(false);
const durchwahlSearchQuery = ref("");

// Kurztexte
const globalKurztexte = ref([]);
const showAllKurztexte = ref(false);
const kurztextSearchDialog = ref(false);
const searchQuery = ref("");

// Kalender & Nutzer
const selectedYear = ref(new Date().getFullYear());
const activeUsers = ref([]);
const upcomingEvents = ref([]);
const showAllUpcoming = ref(false);

// Modelle
const globalModelle = ref([]);
const showAllModelle = ref(false);
const showAddModelle = ref(false);
const showModelleSearch = ref(false);
const showDeleteConfirm = ref(false);
const modelSearchQuery = ref("");
const modelToDelete = ref(null);
const isEditingModel = ref(false);

// Neues Modell-Formular (für Hinzufügen/Bearbeiten)
const newModel = reactive({
  id: null,
  modell: "",
  ws_menue: "",
  ws_zubehoer: false,
  tassenzahl: "",
  kalibrieren: "",
  kalzubehoer: false,
  zuruecksetzen: "",
  zrzubehoer: false,
  temp_min: "",
  temp_max: "",
  sonstiges: "",
  kaffee_temp_min: "",
  kaffee_temp_max: "",
  kaffee_temp_soll: "",
  milch_temp_min: "",
  milch_temp_max: "",
  milch_temp_soll: ""
});

// ======== Zeit & Datum ========
const currentTime = ref(new Date());
const updateTimeInterval = ref(null);

// Hilfsmethoden für die verbesserte Darstellung

// Icon für jeden Widget-Typ zurückgeben
function getHeaderIcon(widgetName) {
  switch (widgetName) {
    case "greeting":
      return "mdi-coffee-maker";
    case "calendar":
      return "mdi-calendar-month";
    case "clock":
      return "mdi-account-multiple";
    case "daily":
      return "mdi-chart-bar";
    case "monthly":
      return "mdi-chart-timeline-variant";
    case "durchwahl":
      return "mdi-phone";
    case "kurztexte":
      return "mdi-text-box";
    case "notes":
      return "mdi-note-text-outline";
    default:
      return "mdi-view-dashboard";
  }
}

// Titel für jeden Widget-Typ zurückgeben
function getCardTitle(widgetName) {
  switch (widgetName) {
    case "greeting":
      return "Modelle Hilfe";
    case "calendar":
      return "Kalender";
    case "clock":
      return "Angemeldete Benutzer";
    case "daily":
      return "Daily Repbox";
    case "monthly":
      return "Monthly Repbox";
    case "durchwahl":
      return "Durchwahlen";
    case "kurztexte":
      return "Kurztexte";
    case "notes":
      return "Meine Notizen";
    default:
      return "Widget";
  }
}

// Text auf eine bestimmte Länge kürzen
function truncateText(text, maxLength) {
  if (!text) return "";
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
}

// Prüfen, ob ein Event nur einen Tag dauert
function isSingleDayEvent(event) {
  return (
    new Date(event.start_date).toLocaleDateString() ===
    new Date(event.end_date).toLocaleDateString()
  );
}

// Datum im lokalen Format anzeigen
function formatDateLocale(dateStr) {
  return new Date(dateStr).toLocaleDateString("de-DE");
}

// Zeit regelmäßig aktualisieren
const startTimeUpdates = () => {
  updateTimeInterval.value = setInterval(() => {
    currentTime.value = new Date();
  }, 1000);
};

// Kalenderwoche berechnen
function getWeekNumber(date) {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  );
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

// ======== Dashboard Layout ========
// Spaltenreferenzen für das Dashboard
const leftColumn = ref([
  { id: 1, name: "clock", collapsed: false },
  { id: 2, name: "calendar", collapsed: false },
]);

const middleColumn = ref([
  { id: 3, name: "notes", collapsed: false },
  { id: 4, name: "daily", collapsed: false },
  { id: 5, name: "monthly", collapsed: false },
]);

const rightColumn = ref([
  { id: 6, name: "greeting", collapsed: false },
  { id: 7, name: "kurztexte", collapsed: false },
  { id: 8, name: "durchwahl", collapsed: false },

]);

// Layout-Management
const LAYOUT_KEY = "dashboardLayout";

async function saveLayout() {
  try {
    const layout = {
      left: leftColumn.value,
      middle: middleColumn.value,
      right: rightColumn.value,
    };

    await axios.post(
      `${API_BASE_URL}dashboard_layout.php`,
      { action: "save", layout },
      { withCredentials: true }
    );

    // Als Backup im localStorage speichern
    localStorage.setItem(LAYOUT_KEY, JSON.stringify(layout));
  } catch (error) {
    console.error("Fehler beim Speichern des Layouts:", error);
    toast.error("Layoutänderungen konnten nicht gespeichert werden");
  }
}

async function loadLayout() {
  try {
    // Zuerst versuchen, von der API zu laden
    const response = await axios.get(`${API_BASE_URL}dashboard_layout.php?action=get`, {
      withCredentials: true,
    });

    if (response.data.status === "success" && response.data.layout) {
      const layout = response.data.layout;
      leftColumn.value = layout.left || leftColumn.value;
      middleColumn.value = layout.middle || middleColumn.value;
      rightColumn.value = layout.right || rightColumn.value;
      return;
    }

    // Als Fallback aus localStorage laden
    const saved = localStorage.getItem(LAYOUT_KEY);
    if (saved) {
      const layout = JSON.parse(saved);
      leftColumn.value = layout.left || leftColumn.value;
      middleColumn.value = layout.middle || middleColumn.value;
      rightColumn.value = layout.right || rightColumn.value;
    }
  } catch (error) {
    console.error("Fehler beim Laden des Layouts:", error);
    // Versuche lokale Version als Fallback
    try {
      const saved = localStorage.getItem(LAYOUT_KEY);
      if (saved) {
        const layout = JSON.parse(saved);
        leftColumn.value = layout.left || leftColumn.value;
        middleColumn.value = layout.middle || middleColumn.value;
        rightColumn.value = layout.right || rightColumn.value;
      }
    } catch (localError) {
      console.error("Lokales Layout konnte nicht geladen werden:", localError);
    }
  }
}

function toggleCollapse(element) {
  element.collapsed = !element.collapsed;
  saveLayout(); // Einstellung speichern
}

// ======== Computed Properties ========
const weekNumber = computed(() => getWeekNumber(currentTime.value));

// Durchwahlen
const displayedDurchwahlen = computed(() =>
  showAllDurchwahlen.value
    ? globalDurchwahlen.value
    : globalDurchwahlen.value.slice(0, 4)
);

const filteredDurchwahlen = computed(() => {
  if (!durchwahlSearchQuery.value) return [];

  const q = durchwahlSearchQuery.value.toLowerCase();
  return globalDurchwahlen.value.filter(
    (item) =>
      (item.name && item.name.toLowerCase().includes(q)) ||
      (item.vorname && item.vorname.toLowerCase().includes(q)) ||
      (item.durchwahl && item.durchwahl.toLowerCase().includes(q)) ||
      (item.abteilung && item.abteilung.toLowerCase().includes(q))
  );
});

// Kurztexte
const displayedKurztexte = computed(() =>
  showAllKurztexte.value
    ? globalKurztexte.value
    : globalKurztexte.value.slice(0, 4)
);

const filteredKurztexte = computed(() => {
  if (!searchQuery.value) return [];

  const q = searchQuery.value.toLowerCase();
  return globalKurztexte.value.filter(
    (item) =>
      (item.kurztext && item.kurztext.toLowerCase().includes(q)) ||
      (item.nummer && String(item.nummer).includes(q))
  );
});

// Modelle
const displayedModelle = computed(() =>
  showAllModelle.value ? globalModelle.value : globalModelle.value.slice(0, 4)
);

const filteredModelle = computed(() => {
  if (!modelSearchQuery.value) return globalModelle.value;

  const q = modelSearchQuery.value.toLowerCase();
  return globalModelle.value.filter((model) =>
    model.modell.toLowerCase().includes(q)
  );
});

// Kalender-Events
const holidayEvents = computed(() => {
  const year = selectedYear.value;
  const hd = new Holidays("DE", "NW");
  const holidaysList = hd.getHolidays(year) || [];

  return holidaysList
    .filter((holiday) => holiday.type === "public")
    .map((holiday, index) => {
      const dateOnly = holiday.date.substring(0, 10);
      return {
        id: `holiday-${index}`,
        title: holiday.name,
        description: "Feiertag in NRW",
        start_date: dateOnly + "T00:00",
        end_date: dateOnly + "T23:59",
        color: "#0000ff",
      };
    });
});

const combinedFeiertageEvents = computed(() => {
  const now = new Date();
  const all = [...holidayEvents.value, ...ferienEvents];

  return all
    .filter((event) => new Date(event.end_date) >= now)
    .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
});

const displayedFeiertageEvents = computed(() =>
  combinedFeiertageEvents.value.slice(0, 3)
);

// ======== Funktionen zum Umschalten der Ansichten ========
function toggleShowAllModelle() {
  showAllModelle.value = !showAllModelle.value;
}

function toggleShowAllDurchwahlen() {
  showAllDurchwahlen.value = !showAllDurchwahlen.value;
}

function toggleShowAllKurztexte() {
  showAllKurztexte.value = !showAllKurztexte.value;
}

// ======== Modell-Management ========
function editModel(model) {
  isEditingModel.value = true;
  Object.assign(newModel, model);
  showAddModelle.value = true;
}

function deleteModel(model) {
  modelToDelete.value = model;
  showDeleteConfirm.value = true;
}

async function confirmDelete() {
  if (!modelToDelete.value?.id) return;

  loading.value = true;

  try {
    const response = await axios.post(
      `${API_BASE_URL}modelle.php`,
      {
        id: modelToDelete.value.id,
        action: "delete",
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success("Modell erfolgreich gelöscht");
      showDeleteConfirm.value = false;
      closeModelDialog();
      await fetchGlobalModelle();
    } else {
      toast.error(response.data.message || "Fehler beim Löschen des Modells");
    }
  } catch (error) {
    console.error("Fehler beim Löschen:", error);
    toast.error("Ein Fehler ist aufgetreten");
  } finally {
    loading.value = false;
    modelToDelete.value = null;
  }
}

function closeModelDialog() {
  showAddModelle.value = false;
  isEditingModel.value = false;

  // Formular zurücksetzen
  Object.assign(newModel, {
    id: null,
    modell: "",
    ws_menue: "",
    ws_zubehoer: false,
    tassenzahl: "",
    kalibrieren: "",
    kalzubehoer: false,
    zuruecksetzen: "",
    zrzubehoer: false,
    temp_min: "",
    temp_max: "",
    sonstiges: "",
    kaffee_temp_min: "",
    kaffee_temp_max: "",
    kaffee_temp_soll: "",
    milch_temp_min: "",
    milch_temp_max: "",
    milch_temp_soll: ""
  });
}

async function saveModel() {
  if (!newModel.modell) {
    toast.info("Bitte geben Sie einen Modellnamen ein");
    return;
  }

  loading.value = true;

  try {
    const action = isEditingModel.value ? "update" : "add";
    const response = await axios.post(
      `${API_BASE_URL}modelle.php`,
      {
        ...newModel,
        action,
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success(
        isEditingModel.value ? "Modell aktualisiert" : "Modell hinzugefügt"
      );
      closeModelDialog();
      await fetchGlobalModelle();
    } else {
      toast.error(response.data.message || "Fehler beim Speichern des Modells");
    }
  } catch (error) {
    console.error("Fehler beim Speichern:", error);
    toast.error("Ein Fehler ist aufgetreten");
  } finally {
    loading.value = false;
  }
}

// ======== API-Zugriff ========
// Dashboard-Daten laden
async function fetchDashboard() {
  loading.value = true;
  try {
    const response = await axios.get(`${API_BASE_URL}dashboard.php`, {
      params: { action: 'stats' },
      withCredentials: true,
    });

    if (response.data.status === "success") {
      dashboardData.value = response.data.dashboard;
    } else {
      console.error("Fehler beim Abrufen:", response.data.message);
    }
  } catch (error) {
    console.error("Fehler beim Abrufen des Dashboards:", error);
  } finally {
    loading.value = false;
  }
}

// Durchwahlen laden
async function fetchGlobalDurchwahlen() {
  try {
    const response = await axios.get(`${API_BASE_URL}extensions.php?action=get`, {
      withCredentials: true
    });

    if (response.data.status === "success") {
      globalDurchwahlen.value = response.data.durchwahlen || [];
    } else {
      console.error(
        "Fehler beim Laden der Durchwahlen:",
        response.data.message
      );
    }
  } catch (error) {
    console.error("Fehler beim Abrufen der globalen Durchwahlen:", error);
  }
}

// Kurztexte laden
async function fetchGlobalKurztexte() {
  try {
    const response = await axios.get(`${API_BASE_URL}textsnippets.php`, {
      withCredentials: true
    });

    if (response.data.status === "success") {
      globalKurztexte.value = response.data.kurztexte || [];
    } else {
      console.error("Fehler beim Laden der Kurztexte:", response.data.message);
    }
  } catch (error) {
    console.error("Fehler beim Abrufen der globalen Kurztexte:", error);
  }
}

// Anstehende Termine laden
async function fetchUpcomingEvents() {
  try {
    const response = await axios.get(`${API_BASE_URL}calendar.php`, {
      params: { upcoming: 1 },
      withCredentials: true,
    });

    if (response.data.status === "success") {
      upcomingEvents.value = response.data.events || [];
    } else {
      console.error(
        "Fehler beim Abrufen der anstehenden Termine:",
        response.data.message
      );
    }
  } catch (error) {
    console.error("Fehler beim Abrufen der anstehenden Termine:", error);
  }
}

// Aktive Benutzer laden
async function fetchActiveUsers() {
  try {
    const response = await axios.get(`${API_BASE_URL}activeUsers.php?action=get_active_users`, {
      withCredentials: true,
    });

    if (response.data.status === "success") {
      activeUsers.value = response.data.users || [];
    } else {
      console.error("Fehler beim Laden der Benutzer:", response.data.message);
    }
  } catch (error) {
    console.error("Fehler beim Abrufen der aktiven Benutzer:", error);
  }
}

// Modelle laden
async function fetchGlobalModelle() {
  try {
    const response = await axios.get(`${API_BASE_URL}modelle.php`, {
      withCredentials: true,
    });

    if (response.data.status === "success") {
      globalModelle.value = response.data.modelle || [];
    } else {
      console.error("Fehler beim Laden der Modelle:", response.data.message);
    }
  } catch (error) {
    console.error("Fehler beim Abrufen der Modelle:", error);
  }
}

// ======== Formatierungsfunktionen ========
// Zeit formatieren (HH:MM)
function formatTime(dateStr) {
  const dt = new Date(dateStr);
  const pad = (v) => (v < 10 ? "0" + v : v);
  return `${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

// In den script-Teil der Komponente einfügen
const copyToClipboard = (text) => {
  try {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success(`Text in die Zwischenablage kopiert!`);
      })
      .catch((err) => {
        console.error("Fehler beim Kopieren in die Zwischenablage:", err);
        toast.error("Kopieren fehlgeschlagen");
      });
  } catch (err) {
    console.error("Fehler beim Kopieren in die Zwischenablage:", err);
    toast.error("Kopieren fehlgeschlagen");
  }
};

// Notizen-Dialoge und -Funktionen
const notesSearchQuery = ref("");

// Notizen-Funktionen
function showNotesSearch() {
  showNotesSearchDialog.value = true;
}

function showAddNote() {
  showAddNoteDialog.value = true;
}

// Fenstergrößen und responsive Anpassungen hinzufügen (falls noch nicht vorhanden)
const windowWidth = ref(window.innerWidth);
const isMobile = computed(() => windowWidth.value < 960);

// ======== Lebenszyklus-Hooks ========
onMounted(() => {
  loadLayout();
  fetchDashboard();
  fetchGlobalDurchwahlen();
  fetchGlobalKurztexte();
  fetchUpcomingEvents();
  fetchActiveUsers();
  fetchGlobalModelle();
  startTimeUpdates();
  window.addEventListener("resize", () => {
    windowWidth.value = window.innerWidth;
  });
});

onBeforeUnmount(() => {
  if (updateTimeInterval.value) {
    clearInterval(updateTimeInterval.value);
  }
  window.removeEventListener("resize", () => {
    windowWidth.value = window.innerWidth;
  });
});

// Provide gemeinsam genutzte Methoden und Daten
provide("getHeaderIcon", getHeaderIcon);
provide("getCardTitle", getCardTitle);
provide("displayedFeiertageEvents", displayedFeiertageEvents);
provide("combinedFeiertageEvents", combinedFeiertageEvents); // Diese Zeile hinzufügen
provide("upcomingEvents", upcomingEvents);
provide("showAllUpcoming", showAllUpcoming);
provide("formatDateLocale", formatDateLocale);
provide("formatTime", formatTime);
provide("isSingleDayEvent", isSingleDayEvent);
provide("toggleShowAllUpcoming", toggleShowAllUpcoming);
// Füge diese zusätzlichen provides zu DashboardView.vue hinzu
provide("globalDurchwahlen", globalDurchwahlen);
provide("displayedDurchwahlen", displayedDurchwahlen);
provide("showAllDurchwahlen", showAllDurchwahlen);
provide("toggleShowAllDurchwahlen", toggleShowAllDurchwahlen);

provide("globalKurztexte", globalKurztexte);
provide("displayedKurztexte", displayedKurztexte);
provide("showAllKurztexte", showAllKurztexte);
provide("toggleShowAllKurztexte", toggleShowAllKurztexte);

provide("currentTime", currentTime);
provide("dashboardData", dashboardData);
provide("activeUsers", activeUsers);
provide("formatTimeDisplay", formatTimeDisplay);
provide("weekNumber", weekNumber);
// Füge diese zusätzlichen provides zu DashboardView.vue hinzu
provide("getStatLabel", getStatLabel);
provide("getStatColor", getStatColor);
provide("calculatePercentage", calculatePercentage);

// Ergänze diese zusätzlichen provides im Script-Setup-Bereich von DashboardView.vue
provide("displayedModelle", displayedModelle);
provide("globalModelle", globalModelle);
provide("showAllModelle", showAllModelle);
provide("toggleShowAllModelle", toggleShowAllModelle);
provide("editModel", editModel);
provide("truncateText", truncateText);
provide("toggleShowAllUpcoming", () => {
  showAllUpcoming.value = !showAllUpcoming.value;
});
provide("copyToClipboard", copyToClipboard);
provide("isMobile", isMobile);

// ======== Schulferien NRW 2025-2028 ========
// Manuell definierte Ferien (konstant, nicht reaktiv)
const ferienEvents = [
  // 2025
  {
    id: "ferien-2025-oster",
    title: "Osterferien",
    description: "Osterferien NRW 2025",
    start_date: "2025-04-14T00:00",
    end_date: "2025-04-26T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2025-sommer",
    title: "Sommerferien",
    description: "Sommerferien NRW 2025",
    start_date: "2025-07-16T00:00",
    end_date: "2025-08-28T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2025-herbst",
    title: "Herbstferien",
    description: "Herbstferien NRW 2025",
    start_date: "2025-10-13T00:00",
    end_date: "2025-10-25T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2025-weihnachts",
    title: "Weihnachtsferien",
    description: "Weihnachtsferien NRW 2025",
    start_date: "2025-12-23T00:00",
    end_date: "2026-01-06T23:59",
    color: "#2196F3",
  },
  // 2026
  {
    id: "ferien-2026-oster",
    title: "Osterferien",
    description: "Osterferien NRW 2026",
    start_date: "2026-03-29T00:00",
    end_date: "2026-04-09T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2026-sommer",
    title: "Sommerferien",
    description: "Sommerferien NRW 2026",
    start_date: "2026-07-15T00:00",
    end_date: "2026-08-27T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2026-herbst",
    title: "Herbstferien",
    description: "Herbstferien NRW 2026",
    start_date: "2026-10-12T00:00",
    end_date: "2026-10-24T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2026-weihnachts",
    title: "Weihnachtsferien",
    description: "Weihnachtsferien NRW 2026",
    start_date: "2026-12-23T00:00",
    end_date: "2027-01-06T23:59",
    color: "#2196F3",
  },
  // 2027
  {
    id: "ferien-2027-oster",
    title: "Osterferien",
    description: "Osterferien NRW 2027",
    start_date: "2027-04-03T00:00",
    end_date: "2027-04-15T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2027-sommer",
    title: "Sommerferien",
    description: "Sommerferien NRW 2027",
    start_date: "2027-07-14T00:00",
    end_date: "2027-08-26T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2027-herbst",
    title: "Herbstferien",
    description: "Herbstferien NRW 2027",
    start_date: "2027-10-11T00:00",
    end_date: "2027-10-23T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2027-weihnachts",
    title: "Weihnachtsferien",
    description: "Weihnachtsferien NRW 2027",
    start_date: "2027-12-23T00:00",
    end_date: "2028-01-06T23:59",
    color: "#2196F3",
  },
  // 2028
  {
    id: "ferien-2028-oster",
    title: "Osterferien",
    description: "Osterferien NRW 2028",
    start_date: "2028-03-25T00:00",
    end_date: "2028-04-06T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2028-sommer",
    title: "Sommerferien",
    description: "Sommerferien NRW 2028",
    start_date: "2028-07-12T00:00",
    end_date: "2028-08-24T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2028-herbst",
    title: "Herbstferien",
    description: "Herbstferien NRW 2028",
    start_date: "2028-10-09T00:00",
    end_date: "2028-10-21T23:59",
    color: "#2196F3",
  },
  {
    id: "ferien-2028-weihnachts",
    title: "Weihnachtsferien",
    description: "Weihnachtsferien NRW 2028",
    start_date: "2028-12-23T00:00",
    end_date: "2029-01-06T23:59",
    color: "#2196F3",
  },
];

// Notizen
const globalNotes = ref([]);
const editingNote = ref(false);
const newNote = reactive({
  id: null,
  title: "",
  content: "",
  created_at: null,
  updated_at: null
});

// Computed Property für gefilterte Notizen
const filteredNotes = computed(() => {
  if (!notesSearchQuery.value) return [];

  const q = notesSearchQuery.value.toLowerCase();
  return globalNotes.value.filter(
    note =>
      (note.title && note.title.toLowerCase().includes(q)) ||
      (note.content && note.content.toLowerCase().includes(q))
  );
});

// Notizen-Funktionen
function editNote(note) {
  editingNote.value = true;
  Object.assign(newNote, note);
  showAddNoteDialog.value = true;
  showNotesSearchDialog.value = false;
}

function closeNoteDialog() {
  showAddNoteDialog.value = false;
  editingNote.value = false;
  // Formular zurücksetzen
  Object.assign(newNote, {
    id: null,
    title: "",
    content: "",
    created_at: null,
    updated_at: null
  });
}

async function saveNote() {
  if (!newNote.content) {
    toast.info("Bitte geben Sie einen Inhalt für die Notiz ein");
    return;
  }

  loading.value = true;

  try {
    const action = editingNote.value ? "update" : "add";
    const response = await axios.post(
      `${API_BASE_URL}notes.php`,
      {
        ...newNote,
        action
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success(
        editingNote.value ? "Notiz aktualisiert" : "Notiz hinzugefügt"
      );
      closeNoteDialog();
      await fetchGlobalNotes(); // Notizen neu laden
    } else {
      toast.error(response.data.message || "Fehler beim Speichern der Notiz");
    }
  } catch (error) {
    console.error("Fehler beim Speichern:", error);
    toast.error("Ein Fehler ist aufgetreten");
  } finally {
    loading.value = false;
  }
}

async function deleteNote() {
  if (!newNote.id) return;

  loading.value = true;

  try {
    const response = await axios.post(
      `${API_BASE_URL}notes.php`,
      {
        id: newNote.id,
        action: "delete"
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      toast.success("Notiz erfolgreich gelöscht");
      closeNoteDialog();
      await fetchGlobalNotes(); // Notizen neu laden
    } else {
      toast.error(response.data.message || "Fehler beim Löschen der Notiz");
    }
  } catch (error) {
    console.error("Fehler beim Löschen:", error);
    toast.error("Ein Fehler ist aufgetreten");
  } finally {
    loading.value = false;
  }
}

// Notizen laden
async function fetchGlobalNotes() {
  try {
    const response = await axios.get(`${API_BASE_URL}notes.php?fetch=1`, {
      withCredentials: true
    });

    if (response.data.status === "success") {
      globalNotes.value = response.data.notes || [];
    } else {
      console.error("Fehler beim Laden der Notizen:", response.data.message);
    }
  } catch (error) {
    console.error("Fehler beim Abrufen der Notizen:", error);
  }
}

// Dialog-Steuerung über provide/inject-Mechanism für Notes Widget
const showNotesSearchDialog = ref(false);
const showAddNoteDialog = ref(false);

// Provide Variablen für Widget-Kommunikation
provide('showNotesSearchDialog', showNotesSearchDialog);
provide('showAddNoteDialog', showAddNoteDialog);

// Event-Handlers für NotesWidget-Buttons in der Kopfzeile
function handleShowNotesSearch() {
  showNotesSearchDialog.value = true;
}

function handleAddNote() {
  showAddNoteDialog.value = true;
}
</script>

<style scoped>
/* ======== Grundlayout und Container ======== */
.dashboard-container {
  position: relative;
  color: rgba(255, 255, 255, 0.9);
  min-height: 100vh;
  padding-top: 16px;
  padding-bottom: 32px;
  z-index: 1;
}

/* ======== Hintergrundelemente ======== */
.background-elements {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 0;
}

.ambient-light {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.15;
}

.ambient-light-1 {
  width: 600px;
  height: 600px;
  top: -200px;
  right: -100px;
  animation: pulse 15s infinite alternate;
}

.ambient-light-2 {
  width: 500px;
  height: 500px;
  bottom: -100px;
  left: -100px;
  animation: pulse 20s infinite alternate-reverse;
}

.ambient-light-3 {
  width: 400px;
  height: 400px;
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: pulse 18s infinite alternate;
}

@keyframes pulse {
  0% {
    opacity: 0.1;
    transform: scale(1);
  }
  50% {
    opacity: 0.15;
    transform: scale(1.05);
  }
  100% {
    opacity: 0.1;
    transform: scale(1);
  }
}

.grid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

/* ======== Mobile Info-Alert ======== */
.mobile-alert {
  background: rgba(76, 175, 80, 0.1) !important;
  border-left: 3px solid rgba(76, 175, 80, 0.7) !important;
  border-radius: 8px;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
}

/* ======== Spalten und Drag-Bereich ======== */
.column-dropzone {
  min-height: 200px;
  padding-bottom: 20px;
  z-index: 2;
  position: relative;
}

/* ======== Dialog-Karten ======== */
.dialog-card {
  background: rgba(25, 25, 30, 0.85) !important;
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.dialog-title {
  padding: 16px 20px;
  border-bottom: none;
  position: relative;
}

.title-text {
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0.3px;
  -webkit-background-clip: text;
  background-clip: text;
}

.dialog-divider {
  border-color: rgba(255, 255, 255, 0.06);
  margin: 0;
}

.dialog-content {
  padding: 20px;
  color: rgba(255, 255, 255, 0.9);
}

.dialog-actions {
  padding: 12px 20px;
  background: rgba(20, 20, 30, 0.3);
}

.warning-header {
  background: linear-gradient(
    90deg,
    rgba(244, 67, 54, 0.2),
    rgba(244, 67, 54, 0.1),
    transparent
  );
}

/* ======== Formularelemente ======== */
.form-field {
  margin-bottom: 16px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.form-field :deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.9);
}

.form-field :deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.12);
}

.form-field:hover :deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.18);
}

.form-field :deep(.v-field__prepend-inner .v-icon) {
  color: rgba(76, 175, 80, 0.9) !important;
}

/* ======== Buttons ======== */
.submit-btn {
  height: 44px;
  border-radius: 12px !important;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.submit-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  transition: 0.6s;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(76, 175, 80, 0.4) !important;
}

.submit-btn:hover:not(:disabled)::before {
  left: 100%;
}

.submit-btn:disabled {
  opacity: 0.7;
  box-shadow: none !important;
}

.delete-btn:hover {
  background-color: rgba(244, 67, 54, 0.1);
}

.delete-confirm-btn {
  height: 44px;
  border-radius: 12px !important;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3) !important;
  transition: all 0.3s ease;
}

.delete-confirm-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(244, 67, 54, 0.4) !important;
}

.add-btn:hover {
  background-color: rgba(76, 175, 80, 0.1);
}

/* ======== Suchergebnisse ======== */
.search-results {
  max-height: 350px;
  overflow-y: auto;
  background: rgba(30, 30, 40, 0.3);
  border-radius: 8px;
  padding: 4px;
  border: 0px solid rgba(255, 255, 255, 0.05);
}

.search-result-item {
  margin-bottom: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  background: rgba(30, 30, 40, 0.5);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.search-result-item:hover {
  background-color: rgba(35, 35, 45, 0.6);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.empty-results {
  background: rgba(30, 30, 40, 0.3);
  border-radius: 8px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.search-placeholder {
  background: rgba(30, 30, 40, 0.3);
  border-radius: 8px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

/* ======== Durchwahlen & Kurztexte ======== */
.extension-name {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.extension-department {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.6);
}

.extension-chip {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid rgba(76, 175, 80, 0.5);
}

.extension-chip:hover {
  background-color: rgba(76, 175, 80, 0.1);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.2);
}

.snippet-number {
  min-width: 30px;
  display: flex;
  justify-content: center;
  border: 1px solid rgba(76, 175, 80, 0.5);
}

.snippet-title {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  flex: 1;
}

.snippet-text {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 4px;
}

.snippet-actions {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.search-result-item:hover .snippet-actions {
  opacity: 1;
}

/* ======== Scrollbar-Styling ======== */
.search-results::-webkit-scrollbar {
  width: 6px;
}

.search-results::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

.search-results::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
}

.search-results::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* ======== Temperatur-Sektionen Styling ======== */
.temperature-section {
  background: rgba(30, 30, 40, 0.3);
  border-radius: 12px;
  padding: 14px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.temperature-heading {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}

.temperature-heading .v-icon {
  opacity: 0.9;
}

/* ======== Responsive Anpassungen ======== */
@media (max-width: 960px) {
  .dialog-card {
    border-radius: 16px;
  }

  .dialog-title {
    padding: 14px 16px;
  }

  .title-text {
    font-size: 16px;
  }

  .dialog-content {
    padding: 16px;
  }

  .dialog-actions {
    padding: 10px 16px;
  }
}

@media (max-width: 600px) {
  .column-dropzone {
    min-height: 100px;
  }

  .dialog-card {
    border-radius: 14px;
  }

  .dialog-title {
    padding: 12px 14px;
  }

  .title-text {
    font-size: 15px;
  }

  .dialog-content {
    padding: 14px;
  }

  .dialog-actions {
    padding: 8px 14px;
  }
}
</style>
